import { IEntity, IEquatable } from './entity';
import { ILocalizable, ILocalized } from './localized';
import { IMedia } from './media';
import { IPageRelation } from './page';

export enum MenuCategoryStrategy {
  Default = 'default', // show hidden categories recursively,
  ExcludeHidden = 'exclude-hidden',
  ExcludeRoute = 'exclude-route',
  ExcludeHiddenAndRoute = 'exclude-hidden-and-route',
  /*
  show direct children hidden categories, (future implementation)
  show children hidden categories with maximum depth, (future implementation)
  show specific hidden categories (future implementation)
  */
}

export type IMenuBase = {
  abstract?: ILocalizable;
  extra?: ILocalizable;
  customClass?: string;
  id: IEquatable;
  items?: IMenuItem[];
  media?: IMedia;
  title: ILocalizable;
};

export type IMenuCategory = IMenuBase & {
  type: 'category';
  href: string;
  category: IEquatable;
  customTitle?: ILocalizable;
};

export type IMenuGroup = IMenuBase & {
  type: 'group';
};

export type IMenuLink = IMenuBase & {
  type: 'link';
  href: string;
  target: string;
};

export type IMenuRoute = IMenuBase & {
  type: 'route';
  href: string;
  page: IEquatable;
  schema: string;
};

export type IMenuPage = IMenuBase & {
  type: 'page';
  href: string;
  page: IEquatable;
  schema: string;
  customTitle?: ILocalizable;
};

export type IMenuCustom = Record<string, unknown> & {
  id: IEquatable;
  type: string;
};

export type IMenuItem = IMenuCategory | IMenuGroup | IMenuLink | IMenuPage | IMenuRoute;

export type IMenuHref = IMenuCategory | IMenuLink | IMenuPage | IMenuRoute;

export function isMenuHref(item: IMenuItem): item is IMenuHref {
  return ['category', 'link', 'page', 'route'].includes(item.type);
}

export function isMenuCategory(item: IMenuItem): item is IMenuCategory {
  return item.type === 'category';
}

export function isMenuGroup(item: IMenuItem): item is IMenuGroup {
  return item.type === 'group';
}

export function isMenuLink(item: IMenuItem): item is IMenuLink {
  return item.type === 'link';
}

export function isMenuPage(item: IMenuItem): item is IMenuPage {
  return item.type === 'page';
}

export function isMenuRoute(item: IMenuItem): item is IMenuRoute {
  return item.type === 'route';
}

/*
export type IMenuItem = {
  id: IEquatable;
  type: 'category' | 'group' | 'link' | 'route' | 'page' | string;
  title: ILocalizable;
  abstract?: string;
  media?: IMedia;
  href?: string;
  target?: string;
  items: IMenuItem[];
};
*/

export type IMenu = {
  id: string;
  items: IMenuItem[];
};

export type ILocalizedMenu = {
  id: string;
  items: ILocalized<IMenuItem>[];
};

/**
 * this is the original menu model from cms
 */

export type IUndecoratedMenuBase = {
  abstract?: ILocalizable;
  extra?: ILocalizable;
  customClass?: string;
  id?: string;
  items: IUndecoratedMenuItem[];
  markets?: string[];
  media?: IMedia;
  type: string;
};

export type IUndecoratedMenuCategory = Omit<IUndecoratedMenuBase, 'type'> & {
  type: 'category';
  category: string;
  maxDepth?: number;
  strategy?: MenuCategoryStrategy;
  title?: ILocalizable;
};

export type IUndecoratedMenuGroup = Omit<IUndecoratedMenuBase, 'type'> & {
  type: 'group';
  title: ILocalizable;
};

export type IUndecoratedMenuLink = Omit<IUndecoratedMenuBase, 'type'> & {
  type: 'link';
  title: ILocalizable;
  href: string;
  target?: string;
};

export type IUndecoratedMenuPage = Omit<IUndecoratedMenuBase, 'type'> & {
  type: 'page';
  page: IPageRelation;
  title?: ILocalizable;
};

/*
export type IUndecoratedMenuRoute = Omit<IUndecoratedMenuBase, 'type'> & {
  type: 'route';
  title: string;
  href: string;
};
*/

export type IUndecoratedMenuCustom = Omit<IUndecoratedMenuBase, 'type'> & {
  type: 'custom';
  blockType: string;
};

export type IUndecoratedMenuItem = IUndecoratedMenuCategory | IUndecoratedMenuGroup | IUndecoratedMenuLink | IUndecoratedMenuPage | IUndecoratedMenuCustom;

export type IUndecoratedMenu = IEntity & {
  id: string;
  items: IUndecoratedMenuItem[];
};
